// app.auth.flow() — a HEADLESS sign-in / sign-up state machine.
//
// The `app.auth.*` methods are correct primitives but not a flow: a login box
// still has to hand-write identifier-first branching, the challenge loop
// (EMAIL_OTP / MFA / temp-password rotation), the passwordless
// generate-then-silent-sign-in dance, and the provider redirect — each a
// different `app.auth.*` call with its own error handling. Every builder
// re-derives the same few hundred lines (our own console did, twice).
//
// This machine encodes that orchestration once. A UI renders `state.step` and
// calls the transitions; it needs to know NOTHING about Cognito. It is
// framework-agnostic: `subscribe()` is a plain observer, so React/Vue/Svelte
// or vanilla all drive it the same way. The config (which methods, is signup
// open) comes from `enabledMethods()` — the UI is dynamic by construction.
//
// Not included by design: it drives `app.auth` + the provider redirect only.
// It never touches S3/STS — once `step: "done"` fires, the session is already
// adopted on the client (via `onSession`), exactly as a direct `app.auth`
// call would leave it.

import type { AuthChallenge, AuthModule, SignInResult } from "./auth.ts";
import type { OidcSession } from "./oidc.ts";

/** The published sign-in config the machine renders from (mirror of
 *  `AuthModule.enabledMethods`'s return). */
export interface EnabledMethods {
  password: boolean;
  emailCode: boolean;
  /** WEB_AUTHN is an enabled first factor — offer "Sign in with passkey". */
  passkey: boolean;
  /** "email" (default): the identifier is an email address. "handle": a
   *  free-form handle on an email-less pool — no email OTP, no self-service
   *  reset, sign-ups auto-confirm (the runtime's PreSignUp trigger). */
  usernameMode: "email" | "handle";
  providers: string[];
  signupOpen: boolean;
}

/** What the caller is trying to do. Set before `identify`, or inferred:
 *  the machine defaults to sign-in. */
export type AuthIntent = "signin" | "signup";

/** The single, flat, discriminated-by-`step` state a UI renders. Flat (not a
 *  union per step) so a React consumer can read `state.error`/`state.busy`
 *  without narrowing first — the fields not relevant to a step are simply
 *  absent. */
export interface AuthFlowState {
  /** loading  → fetching config (initial)
   *  identify → awaiting the email/username
   *  choose   → email known, MULTIPLE sign-in methods — pick one
   *  password → awaiting a password (sign-in) or a new-account password (sign-up)
   *  verify   → sign-up: awaiting the emailed confirmation code
   *  challenge→ a Cognito challenge is live (EMAIL_OTP / MFA / NEW_PASSWORD_REQUIRED)
   *  reset    → forgot-password: request a code, then set a new password
   *  done     → session adopted; nothing more to render */
  step: "loading" | "identify" | "choose" | "password" | "verify" | "challenge" | "reset" | "done";
  intent: AuthIntent;
  /** Populated once config loads; the UI reads this to decide which buttons/
   *  fields to show. Empty-ish placeholder while `step === "loading"`. */
  methods: EnabledMethods;
  email: string;
  /** step === "challenge": which challenge is live (labels the input). */
  challenge?: AuthChallenge;
  /** step === "reset": "request" collects the email, "confirm" the code+new pw. */
  resetStage?: "request" | "confirm";
  /** step === "done": the adopted session (also live on the client already). */
  session?: OidcSession;
  /** An async transition is in flight — disable inputs, show a spinner. */
  busy: boolean;
  /** Last error, human-readable. Cleared on the next transition. */
  error?: string;
}

export interface AuthFlow {
  /** Current state (synchronous snapshot). */
  get(): AuthFlowState;
  /** Observe state changes. Calls `fn` immediately with the current state,
   *  then on every change. Returns an unsubscribe function. */
  subscribe(fn: (state: AuthFlowState) => void): () => void;

  /** Choose sign-in vs sign-up up front (a tab toggle). Safe any time before
   *  a terminal step; resets nothing but the intent. */
  setIntent(intent: AuthIntent): void;

  /** Submit the identifier. Advances to `choose` (multiple methods),
   *  `password` (password is the only/first method), or `challenge`
   *  (email-code is the only method → a code is sent immediately). For
   *  sign-up it always advances to `password` (a new-account password, real
   *  or sealed-passwordless). */
  identify(email: string): Promise<void>;

  /** From `choose` (or as the single method): use the password path.
   *  Sign-in → authenticate; sign-up → create the account then `verify`. */
  usePassword(password: string): Promise<void>;
  /** From `choose` (or as the single method): email a one-time code and move
   *  to `challenge`. Sign-in only. */
  useEmailCode(): Promise<void>;
  /** From `choose`: passkey sign-in — runs the whole WebAuthn dance (must be
   *  called from a user gesture). Sign-in only; enrolling a passkey is
   *  `auth.registerPasskey` once signed in. */
  usePasskey(): Promise<void>;
  /** Redirect to an external IdP (hosted-UI). Navigates away; the promise
   *  usually does not resolve. `name` is a value from `methods.providers`. */
  useProvider(name: string): Promise<void>;

  /** Answer the live `challenge`. For code-shaped challenges pass the code;
   *  for NEW_PASSWORD_REQUIRED pass the new password. */
  answer(value: string): Promise<void>;
  /** Sign-up `verify`: confirm the emailed code, then sign in silently
   *  (passwordless-aware — uses the password minted at sign-up). */
  confirmSignup(code: string): Promise<void>;
  /** Re-send the sign-up confirmation code (from `verify`). */
  resend(): Promise<void>;

  /** Enter the forgot-password sub-flow (from `choose`/`password`). */
  startReset(): void;
  /** Jump straight to the reset CONFIRM stage with a code already in hand —
   *  for a link-based reset page that lands with `?email=&code=` from the
   *  email link. The UI then shows only a new-password field and calls
   *  `submitReset(code, newPassword)`. Skips the "email me a code" round. */
  resumeReset(email: string): void;
  /** `reset`/request: email a reset code and move to the confirm stage. */
  requestReset(): Promise<void>;
  /** `reset`/confirm: set the new password with the emailed code, then sign in. */
  submitReset(code: string, newPassword: string): Promise<void>;

  /** Go back one step (choose → identify, challenge → choose, …). */
  back(): void;
  /** Reset to the initial identify step (keeps the loaded config). */
  restart(): void;
}

export interface AuthFlowConfig {
  auth: AuthModule;
  /** The hosted-UI redirect (i.e. `app.signIn`). Called by `useProvider`. */
  startProviderRedirect: (name: string) => Promise<void>;
  /** Pre-loaded methods (skips the async fetch — the client already has
   *  them). If omitted the machine calls `auth.enabledMethods()`. */
  methods?: EnabledMethods;
}

const LOADING_METHODS: EnabledMethods = {
  password: true,
  emailCode: false,
  passkey: false,
  usernameMode: "email",
  providers: [],
  signupOpen: true,
};

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
/** Handle-mode identifiers: 3–30 chars, a-z 0-9 and inner hyphens
 *  (case-folded before validation — pools are case-insensitive). */
const HANDLE_RE = /^[a-z0-9](?:[a-z0-9-]{1,28}[a-z0-9])?$/;

/** Cognito rejects sign-in for an account whose email was never verified. It's
 *  not a retryable error — it needs the verify sub-flow — so we detect it and
 *  route there instead of surfacing a dead-end message. */
function isUnconfirmed(e: unknown): boolean {
  const s = e instanceof Error ? `${e.name} ${e.message}` : String(e);
  return /UserNotConfirmed/i.test(s);
}

export function createAuthFlow(cfg: AuthFlowConfig): AuthFlow {
  let state: AuthFlowState = {
    step: "loading",
    intent: "signin",
    methods: cfg.methods ?? LOADING_METHODS,
    email: "",
    busy: false,
  };
  const listeners = new Set<(s: AuthFlowState) => void>();

  function emit() {
    const snapshot = state;
    for (const fn of listeners) fn(snapshot);
  }
  function set(patch: Partial<AuthFlowState>) {
    state = { ...state, ...patch };
    emit();
  }

  // In-memory only: the password minted (or entered) at sign-up, replayed for
  // the silent sign-in right after confirmation. Never persisted, never emitted.
  let signupPassword: string | null = null;

  // The opaque Cognito continuation token that accompanies a challenge —
  // `respondToChallenge` needs it back. Held in the closure, never rendered
  // into state (not a UI concern, and shouldn't leak into logs).
  let challengeSession: string | undefined;

  // Load config unless the caller supplied it. If loading fails, fall back to
  // password-only (the always-present method) so the box still works.
  const ready: Promise<void> = cfg.methods
    ? (set({ step: "identify" }), Promise.resolve())
    : cfg.auth
        .enabledMethods()
        .then((m) => set({ methods: m, step: "identify" }))
        .catch(() => set({ methods: LOADING_METHODS, step: "identify" }));

  /** Run an async transition with uniform busy/error handling. */
  async function guard(fn: () => Promise<void>): Promise<void> {
    if (state.busy) return;
    set({ busy: true, error: undefined });
    try {
      await fn();
    } catch (e) {
      set({ error: e instanceof Error ? e.message : String(e) });
    } finally {
      // `set` inside fn may have moved us to `done`; only clear busy.
      set({ busy: false });
    }
  }

  /** Fold a SignInResult into the state: tokens → done, challenge → challenge. */
  function settle(r: SignInResult) {
    if (r.status === "ok") {
      signupPassword = null;
      challengeSession = undefined;
      set({ step: "done", session: r.session });
    } else {
      challengeSession = r.session;
      set({ step: "challenge", challenge: r.challenge });
    }
  }

  /** Which concrete methods does this pool offer for sign-in? */
  function signinMethods(): Array<"passkey" | "password" | "emailCode" | "provider"> {
    const m = state.methods;
    const out: Array<"passkey" | "password" | "emailCode" | "provider"> = [];
    if (m.passkey) out.push("passkey");
    if (m.password) out.push("password");
    if (m.emailCode) out.push("emailCode");
    if (m.providers.length > 0) out.push("provider");
    return out;
  }

  const handleMode = () => state.methods.usernameMode === "handle";

  return {
    get: () => state,
    subscribe(fn) {
      listeners.add(fn);
      fn(state);
      return () => listeners.delete(fn);
    },

    setIntent(intent) {
      if (state.step === "done") return;
      set({ intent, error: undefined });
    },

    async identify(email) {
      await ready;
      // Handle mode: the identifier is a handle, not an address. Case-fold
      // (the pool is case-insensitive) and validate the handle shape.
      const trimmed = handleMode() ? email.trim().toLowerCase() : email.trim();
      if (handleMode() ? !HANDLE_RE.test(trimmed) : !EMAIL_RE.test(trimmed)) {
        set({
          email: trimmed,
          error: handleMode()
            ? "Enter a valid handle (3–30 chars: a-z, 0-9, hyphens)."
            : "Enter a valid email address.",
        });
        return;
      }
      set({ email: trimmed, error: undefined });
      if (state.intent === "signup") {
        // Sign-up always collects a password step (real, or sealed for a
        // passwordless pool — the field is simply hidden by the UI then).
        set({ step: "password" });
        return;
      }
      // Sign-in: branch on the available methods.
      const methods = signinMethods();
      if (methods.length === 0) {
        set({ error: "This app has no sign-in methods configured." });
        return;
      }
      if (methods.length === 1) {
        // Skip the chooser — go straight to the one method.
        if (methods[0] === "password") return set({ step: "password" });
        if (methods[0] === "emailCode") return this.useEmailCode();
        // A single passkey or provider still needs a click (user gesture /
        // redirect) — show the chooser.
      }
      set({ step: "choose" });
    },

    async usePasskey() {
      await guard(async () => {
        // The whole WebAuthn dance lives in auth.signInWithPasskey — one user
        // gesture in, a settled session (or a follow-up challenge) out.
        settle(await cfg.auth.signInWithPasskey(state.email));
      });
    },

    async usePassword(password) {
      await guard(async () => {
        if (state.intent === "signup") {
          const pw = state.methods.password ? password : undefined;
          const { confirmed, password: used } = await cfg.auth.signUp(state.email, pw);
          signupPassword = used;
          if (confirmed) {
            // Auto-confirmed pool — sign in straight away.
            settle(await cfg.auth.signIn(state.email, used));
          } else {
            set({ step: "verify" });
          }
        } else {
          try {
            settle(await cfg.auth.signIn(state.email, password));
          } catch (e) {
            if (!isUnconfirmed(e)) throw e;
            // The account exists but its email was never verified. Keep the
            // password for the retry, resend a code, and drop into `verify` —
            // exactly the sign-up tail, reused.
            signupPassword = password;
            await cfg.auth.resendCode(state.email).catch(() => {});
            set({ step: "verify" });
          }
        }
      });
    },

    async useEmailCode() {
      await guard(async () => {
        try {
          settle(await cfg.auth.signInWithEmailOtp(state.email));
        } catch (e) {
          if (!isUnconfirmed(e)) throw e;
          // Passwordless account that never confirmed: send a sign-up code and
          // verify. No password to replay, so confirmSignup falls back to
          // re-identify (the user then requests a fresh OTP).
          await cfg.auth.resendCode(state.email).catch(() => {});
          set({ step: "verify" });
        }
      });
    },

    async useProvider(name) {
      await guard(async () => {
        await cfg.startProviderRedirect(name);
      });
    },

    async answer(value) {
      await guard(async () => {
        if (!state.challenge || !challengeSession) throw new Error("no active challenge");
        const isNewPassword = state.challenge === "NEW_PASSWORD_REQUIRED";
        settle(
          await cfg.auth.respondToChallenge({
            username: state.email,
            challenge: state.challenge,
            // The continuation token captured in `settle` when the challenge
            // arrived — threaded back, never surfaced in the rendered state.
            session: challengeSession,
            ...(isNewPassword ? { newPassword: value } : { code: value }),
          }),
        );
      });
    },

    async confirmSignup(code) {
      await guard(async () => {
        await cfg.auth.confirmSignUp(state.email, code.trim());
        // Silent sign-in with the sign-up password → ONE code, no re-entry.
        if (signupPassword) {
          settle(await cfg.auth.signIn(state.email, signupPassword));
        } else {
          // No stored password (shouldn't happen) — fall back to identify.
          set({ step: "identify" });
        }
      });
    },

    async resend() {
      await guard(async () => {
        await cfg.auth.resendCode(state.email);
      });
    },

    startReset() {
      if (handleMode()) {
        // No email → no reset channel. Access recovery on a handle pool is
        // admin-side (AdminSetUserPassword) or app-level (e.g. a recovery code).
        set({ error: "Password reset isn't available for this app — ask an admin." });
        return;
      }
      set({ step: "reset", resetStage: "request", error: undefined });
    },

    resumeReset(email) {
      set({
        step: "reset",
        resetStage: "confirm",
        email: email.trim(),
        intent: "signin",
        error: undefined,
      });
    },

    async requestReset() {
      await guard(async () => {
        await cfg.auth.forgotPassword(state.email);
        set({ resetStage: "confirm" });
      });
    },

    async submitReset(code, newPassword) {
      await guard(async () => {
        await cfg.auth.confirmForgotPassword(state.email, code.trim(), newPassword);
        settle(await cfg.auth.signIn(state.email, newPassword));
      });
    },

    back() {
      switch (state.step) {
        case "choose":
        case "password":
        case "verify":
          set({ step: "identify", error: undefined });
          break;
        case "challenge":
          set({ step: signinMethods().length > 1 ? "choose" : "identify", challenge: undefined, error: undefined });
          break;
        case "reset":
          if (state.resetStage === "confirm") set({ resetStage: "request", error: undefined });
          else set({ step: "identify", resetStage: undefined, error: undefined });
          break;
        default:
          break;
      }
    },

    restart() {
      signupPassword = null;
      set({
        step: "identify",
        challenge: undefined,
        resetStage: undefined,
        session: undefined,
        error: undefined,
        busy: false,
      });
    },
  };
}
